import { Folder, FolderOpen, FolderPlus, MoreHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { MediaFolder } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { UNFILED } from '../schemas/media-list-search';

export interface FolderTilesProps {
  folders: readonly MediaFolder[] | undefined;
  isLoading?: boolean;
  /** Opens a folder, or the "not in a folder" view with `UNFILED`. */
  onOpen: (folder: string) => void;
  /** Present when the viewer may create, rename and delete folders. */
  onCreate?: () => void;
  onRename?: (folder: MediaFolder) => void;
  onDelete?: (folder: MediaFolder) => void;
}

const tileClass =
  'group focus-visible:ring-ring/50 bg-card hover:border-primary/60 flex w-full flex-col items-start gap-2 rounded-lg border p-3 text-left outline-none transition-colors focus-visible:ring-[3px]';

/** The folder row of the library: one tile per folder, like a file manager's top level. */
export function FolderTiles({
  folders,
  isLoading = false,
  onOpen,
  onCreate,
  onRename,
  onDelete,
}: FolderTilesProps) {
  const { t } = useTranslation('media');
  const canWrite = Boolean(onCreate);
  if (!isLoading && folders?.length === 0 && !canWrite) return null;

  return (
    <section className="flex flex-col gap-2" aria-labelledby="media-folders-heading">
      <h2 id="media-folders-heading" className="text-sm font-medium">
        {t('folders.title')}
      </h2>
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
        {isLoading
          ? Array.from({ length: 4 }).map((_, i) => (
              <li key={i}>
                <Skeleton className="h-[88px] w-full rounded-lg" />
              </li>
            ))
          : folders?.map((folder) => (
              <li key={folder.id} className="relative">
                <button
                  type="button"
                  className={tileClass}
                  onClick={() => onOpen(folder.id)}
                  aria-label={t('folders.open', { name: folder.name })}
                >
                  <Folder className="text-primary size-7" aria-hidden />
                  <span className="w-full truncate pr-6 text-sm font-medium" title={folder.name}>
                    {folder.name}
                  </span>
                  <span className="text-muted-foreground text-xs tabular-nums">
                    {t('folders.count', { count: folder.itemCount })}
                  </span>
                </button>
                {onRename && onDelete ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="absolute top-2 right-2 size-7"
                        aria-label={t('folders.actions', { name: folder.name })}
                      >
                        <MoreHorizontal aria-hidden />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-40">
                      <DropdownMenuItem onSelect={() => onRename(folder)}>
                        {t('folders.rename')}
                      </DropdownMenuItem>
                      <DropdownMenuItem variant="destructive" onSelect={() => onDelete(folder)}>
                        {t('folders.delete')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : null}
              </li>
            ))}
        {!isLoading ? (
          <li>
            <button
              type="button"
              className={cn(tileClass, 'border-dashed')}
              onClick={() => onOpen(UNFILED)}
            >
              <FolderOpen className="text-muted-foreground size-7" aria-hidden />
              <span className="text-sm font-medium">{t('folders.unfiled')}</span>
              <span className="text-muted-foreground text-xs">{t('folders.unfiledHint')}</span>
            </button>
          </li>
        ) : null}
        {!isLoading && onCreate ? (
          <li>
            <button
              type="button"
              className={cn(tileClass, 'border-dashed')}
              onClick={onCreate}
              data-tour="media.library.new-folder"
            >
              <FolderPlus className="text-muted-foreground size-7" aria-hidden />
              <span className="text-sm font-medium">{t('folders.new')}</span>
              <span className="text-muted-foreground text-xs">{t('folders.newHint')}</span>
            </button>
          </li>
        ) : null}
      </ul>
    </section>
  );
}
